import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Droppable, Draggable } from "react-beautiful-dnd";
import TimelineAssetItem from "./TimelineCalendarParts/TimelineAssetItem";
import {
  getTimelineDateKey,
  getTimelineWeekEnd,
  getTimelineWeekStart,
  normalizeTimelineDate,
  TIMELINE_DATE_COLUMN_WIDTH,
  TimelineContext,
} from "../../../../../contexts/TimelineContext";
import { CampaignContext } from "../../../../../contexts/CampaignContext";
import { useReadOnly } from "../../../../../contexts/ReadOnlyContext";
import axiosService from "../../../../../utils/AxiosService";
import { format, eachDayOfInterval, isBefore, isAfter } from "date-fns";
import {
  getTimelineDayState,
  getTimelineItemVisualWidth,
  groupTimelineDatesByWeek,
  isTimelineDateOutsideRange,
  TimelineDateHeader,
  TimelineShowTodayButton,
  timelineDayBackgroundClass,
} from "../../../../Shared/TimelineParts/TimelineCalendarUi";

// Width of a single date column in the calendar grid. The Draggable wrapper
// is sized to this so that react-beautiful-dnd's drop detection (which uses
// the dragged element's center) lines up with the date column under the
// cursor regardless of the visual span of multi-day items.
const DATE_COLUMN_WIDTH = TIMELINE_DATE_COLUMN_WIDTH;

const TimelineDraggableItem = observer(function TimelineDraggableItem({
  asset,
  index,
  rowIndex,
  visualWidth,
}) {
  const [isHovering, setIsHovering] = useState(false);
  const readOnly = useReadOnly();

  return (
    <Draggable
      key={`timeline-${asset.id}`}
      draggableId={`timeline-${asset.id}`}
      index={index}
      type="timelineItem"
      isDragDisabled={readOnly}
    >
      {(provided, snapshot) => {
        const expandedWidth =
          isHovering && !snapshot.isDragging
            ? "fit-content"
            : `${visualWidth}px`;

        const onMouseOver = (e) => {
          if (snapshot.isDragging) return;
          const primaryLabel = e.currentTarget.querySelector(
            "[data-timeline-primary-label]",
          );
          if (
            primaryLabel &&
            primaryLabel.scrollWidth > primaryLabel.clientWidth
          ) {
            setIsHovering(true);
          }
        };

        const wrapperBaseStyle = snapshot.isDragging
          ? {
              ...provided.draggableProps.style,
              width: `${DATE_COLUMN_WIDTH}px`,
              height: "32px",
              zIndex: 20,
            }
          : {
              ...provided.draggableProps.style,
              position: "absolute",
              top: `${rowIndex * 40}px`,
              left: 0,
              width: `${DATE_COLUMN_WIDTH}px`,
              height: "32px",
              zIndex: 10,
            };

        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={wrapperBaseStyle}
          >
            <div
              onMouseOver={onMouseOver}
              onMouseLeave={() => setIsHovering(false)}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: expandedWidth,
                minWidth: `${visualWidth}px`,
                height: "32px",
                pointerEvents: snapshot.isDragging ? "none" : "auto",
              }}
            >
              <TimelineAssetItem timelineAsset={asset} />
            </div>
          </div>
        );
      }}
    </Draggable>
  );
});

export default observer(function TimelineCalendar() {
  const [dates, setDates] = useState([]);
  const [groupedDates, setGroupedDates] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { timelineState, jumpToDate, clearJumpToDate } =
    useContext(TimelineContext);
  const { campaign } = useContext(CampaignContext);
  const [allItems, setAllItems] = useState([]);
  const [germanHolidayByDate, setGermanHolidayByDate] = useState({});
  const minRows = 10;

  const scrollContainerRef = useRef(null);
  const now = new Date();

  const campaignStartDate = normalizeTimelineDate(campaign?.start_date);
  const campaignEndDate = normalizeTimelineDate(campaign?.end_date);

  const clampToCampaignRange = (value) => {
    const parsedDate = normalizeTimelineDate(value);
    if (!parsedDate) return campaignStartDate || now;
    if (campaignStartDate && isBefore(parsedDate, campaignStartDate))
      return campaignStartDate;
    if (campaignEndDate && isAfter(parsedDate, campaignEndDate))
      return campaignEndDate;
    return parsedDate;
  };

  const generateDateRange = (startDate, endDate) => {
    setDates(
      eachDayOfInterval({
        start: new Date(startDate),
        end: new Date(endDate),
      }),
    );
  };

  const visibleYearsKey = useMemo(() => {
    if (!dates.length) return "";
    const years = [...new Set(dates.map((d) => format(d, "yyyy")))].sort();
    return years.join(",");
  }, [dates]);

  useEffect(() => {
    if (!visibleYearsKey) return;

    axiosService
      .get("/api/project_manager/german_public_holidays", {
        params: { years: visibleYearsKey },
      })
      .then((body) => {
        // AxiosService interceptor unwraps response to JSON body
        if (body?.holidays) {
          setGermanHolidayByDate((prev) => ({ ...prev, ...body.holidays }));
        }
      })
      .catch(() => {});
  }, [visibleYearsKey]);

  const germanHolidayName = (date) =>
    germanHolidayByDate[getTimelineDateKey(date)];

  const navigateToStartDate = (startDate) => {
    if (scrollContainerRef.current && dates.length > 0) {
      const clampedDate = clampToCampaignRange(startDate);
      const dateStr = getTimelineDateKey(clampedDate);
      const dateIndex = dates.findIndex(
        (date) => getTimelineDateKey(date) === dateStr,
      );

      if (dateIndex !== -1) {
        const rawScrollPosition =
          dateIndex * DATE_COLUMN_WIDTH -
          scrollContainerRef.current.clientWidth / 2 +
          DATE_COLUMN_WIDTH / 2;
        const maxScrollPosition = Math.max(
          0,
          scrollContainerRef.current.scrollWidth -
            scrollContainerRef.current.clientWidth,
        );
        const scrollPosition = Math.min(
          Math.max(rawScrollPosition, 0),
          maxScrollPosition,
        );

        scrollContainerRef.current.scrollLeft = scrollPosition;
      }
    }
  };

  useEffect(() => {
    if (!jumpToDate || dates.length === 0) return;
    try {
      const clampedJumpDate = clampToCampaignRange(jumpToDate);
      if (!clampedJumpDate) {
        console.warn("Invalid jumpToDate:", jumpToDate);
        clearJumpToDate();
        return;
      }

      const frame = requestAnimationFrame(() => {
        navigateToStartDate(clampedJumpDate);
        clearJumpToDate();
      });
      return () => cancelAnimationFrame(frame);
    } catch (error) {
      console.warn("Error processing jumpToDate:", error);
      clearJumpToDate();
    }
  }, [jumpToDate, dates.length]);

  useEffect(() => {
    if (campaignStartDate && campaignEndDate) {
      generateDateRange(
        getTimelineWeekStart(campaignStartDate),
        getTimelineWeekEnd(campaignEndDate),
      );
      return;
    }

    const fallbackNow = new Date();
    const fallbackStart = getTimelineWeekStart(
      new Date(
        fallbackNow.getFullYear(),
        fallbackNow.getMonth(),
        fallbackNow.getDate() - 14,
      ),
    );
    const fallbackEnd = getTimelineWeekEnd(
      new Date(
        fallbackNow.getFullYear(),
        fallbackNow.getMonth(),
        fallbackNow.getDate() + 28,
      ),
    );
    generateDateRange(fallbackStart, fallbackEnd);
  }, [campaign?.start_date, campaign?.end_date]);

  useEffect(() => setGroupedDates(groupTimelineDatesByWeek(dates)), [dates]);

  useEffect(() => {
    setIsInitialLoad(true);
  }, [campaign?.start_date, campaign?.end_date]);

  useEffect(() => {
    if (
      scrollContainerRef.current &&
      groupedDates.length > 0 &&
      isInitialLoad
    ) {
      const todayInRange = clampToCampaignRange(new Date());
      navigateToStartDate(todayInRange);
      setIsInitialLoad(false);
    }
  }, [groupedDates, isInitialLoad, campaign?.start_date, campaign?.end_date]);

  useEffect(() => {
    const items = new Set();
    dates.forEach((date) => {
      const dateStr = getTimelineDateKey(date);
      timelineState.getItemsForDate(dateStr).forEach((item) => {
        items.add(item.id);
      });
    });
    setAllItems(Array.from(items));
  }, [dates, timelineState.timelineItems]);

  const rowCount = Math.max(allItems.length, minRows);

  return (
    <div className="relative h-full min-h-0 flex-1">
      <div
        ref={scrollContainerRef}
        className="h-full overflow-auto bg-rb-grey dark:bg-rb-dark-mode"
      >
        <div className="flex min-h-full min-w-max">
          {groupedDates.map((weekDates) => (
            <div
              key={`week-${format(weekDates[0], "yyyy-MM-dd")}`}
              className="flex min-h-full flex-col"
            >
              <div className="sticky top-0 z-20 shrink-0">
                {/* Week Row Header */}
                <div
                  className="grid border-b-1 border-r border-rb-widget-border bg-white dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode"
                  style={{
                    gridTemplateColumns: `repeat(${weekDates.length}, minmax(0, ${DATE_COLUMN_WIDTH}px))`,
                  }}
                >
                  {groupTimelineDatesByWeek(weekDates).map(
                    (innerWeekDates, weekIndex) => {
                      const weekStart = getTimelineWeekStart(innerWeekDates[0]);
                      const weekEnd = getTimelineWeekEnd(innerWeekDates[0]);

                      return (
                        <div
                          key={weekIndex}
                          className="whitespace-nowrap p-2 text-center font-semibold dark:text-white"
                          style={{
                            gridColumn: `span ${innerWeekDates.length} / span ${innerWeekDates.length}`,
                          }}
                        >
                          {format(weekStart, "MMM d")} -{" "}
                          {format(weekEnd, "MMM d")}, {format(weekEnd, "yyyy")}
                        </div>
                      );
                    },
                  )}
                </div>

                {/* Date Row Header */}
                <div
                  className="grid border-b border-rb-widget-border bg-white dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode"
                  style={{
                    gridTemplateColumns: `repeat(${weekDates.length}, minmax(0, ${DATE_COLUMN_WIDTH}px))`,
                  }}
                >
                  {weekDates.map((date, index) => {
                    const holidayName = germanHolidayName(date);
                    const isOutsideTimelineRange = isTimelineDateOutsideRange(
                      date,
                      campaignStartDate,
                      campaignEndDate,
                    );
                    const dayState = getTimelineDayState(date, holidayName, {
                      isOutsideTimelineRange,
                    });
                    return (
                      <div
                        key={index}
                        title={holidayName || undefined}
                        className={`border-r border-rb-widget-border text-center dark:border-rb-dark-mode-lighter ${timelineDayBackgroundClass(dayState)}`}
                      >
                        <TimelineDateHeader
                          date={date}
                          holidayName={holidayName}
                          isOutsideTimelineRange={isOutsideTimelineRange}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="relative min-h-0 flex-1">
                <div
                  className="grid h-full min-h-full bg-white dark:bg-rb-dark-mode"
                  style={{
                    gridTemplateColumns: `repeat(${weekDates.length}, minmax(0, ${DATE_COLUMN_WIDTH}px))`,
                    position: "relative",
                    minHeight: `${rowCount * 40}px`,
                  }}
                >
                  {weekDates.map((date) => {
                    const dateStr = getTimelineDateKey(date);
                    const itemsForDate = timelineState.getItemsForDate(dateStr);
                    const holidayName = germanHolidayName(date);
                    const dayColumnClass = timelineDayBackgroundClass(
                      getTimelineDayState(date, holidayName, {
                        isOutsideTimelineRange: isTimelineDateOutsideRange(
                          date,
                          campaignStartDate,
                          campaignEndDate,
                        ),
                      }),
                    );

                    return (
                      <Droppable
                        droppableId={`date-${dateStr}`}
                        key={`date-${dateStr}`}
                        type="timelineItem"
                        // isDropDisabled={isDragging && itemsForDate.length > 0}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            title={holidayName || undefined}
                            className={`relative h-full border-r border-rb-widget-border dark:border-rb-dark-mode-lighter ${
                              snapshot.isDraggingOver
                                ? "bg-rb-green/25 dark:bg-rb-green/25"
                                : dayColumnClass
                            }`}
                            style={{
                              minWidth: `${DATE_COLUMN_WIDTH}px`,
                              minHeight: "40px",
                            }}
                          >
                            {itemsForDate.map((asset, idx) => {
                              const visualWidth = getTimelineItemVisualWidth(
                                asset,
                                timelineState,
                              );

                              return (
                                <TimelineDraggableItem
                                  key={`timeline-${asset.id}`}
                                  asset={asset}
                                  index={idx}
                                  rowIndex={allItems.indexOf(asset.id)}
                                  visualWidth={visualWidth}
                                />
                              );
                            })}
                            <div style={{ display: "none" }}>
                              {provided.placeholder}
                            </div>
                          </div>
                        )}
                      </Droppable>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <TimelineShowTodayButton
        onClick={() => navigateToStartDate(new Date())}
        className="absolute bottom-4 right-4 z-30"
        startDate={campaignStartDate}
        endDate={campaignEndDate}
      />
    </div>
  );
});
